import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, CircleCheck as CheckCircle, EyeOff, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { OrderIssue } from '../../types/orderIssues';
import { IssueTypeBadge, SourceBadge, StatusBadge } from './IssueTypeBadge';
import { ResolveNoteModal } from './ResolveNoteModal';

interface IssueRowProps {
  issue: OrderIssue;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onResolve: (id: string, note?: string) => void;
  onIgnore: (id: string) => void;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const IssueRow: React.FC<IssueRowProps> = ({
  issue,
  selected,
  onToggleSelect,
  onResolve,
  onIgnore,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);

  const addr = issue.recipient_address || {};
  const isActionable = issue.status === 'open' || issue.status === 'under_process';

  const addressParts = [
    addr.street || addr.address1,
    addr.houseNumber,
    addr.postalCode,
    addr.city,
    addr.countryCode,
  ].filter(Boolean);

  const handleGoToShippingLabels = () => {
    navigate(`/shipping-labels?ref=${encodeURIComponent(issue.order_reference)}`);
  };

  return (
    <>
      <tr className={`border-b border-gray-100 dark:border-gray-700/50 transition-colors ${selected ? 'bg-amber-50/50 dark:bg-amber-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}>
        {isActionable && (
          <td className="pl-4 py-3">
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect(issue.id)}
              className="h-4 w-4 text-amber-500 rounded border-gray-300 dark:border-gray-600 focus:ring-amber-500"
            />
          </td>
        )}
        {!isActionable && <td className="pl-4 py-3" />}

        <td className="px-3 py-3">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
              {issue.order_reference}
            </span>
            {issue.email_sent_at && (
              <span title={`Emailed ${formatDate(issue.email_sent_at)}`}>
                <Mail className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
              </span>
            )}
          </div>
          {issue.attempt_count > 1 && (
            <span className="text-xs text-red-500 font-medium">
              {issue.attempt_count} attempts
            </span>
          )}
        </td>

        <td className="px-3 py-3">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {issue.client_name || '-'}
          </span>
        </td>

        <td className="px-3 py-3">
          <IssueTypeBadge type={issue.issue_type} />
        </td>

        <td className="px-3 py-3 max-w-xs">
          <p className="text-sm text-gray-600 dark:text-gray-400 truncate" title={issue.issue_description}>
            {issue.issue_description}
          </p>
        </td>

        <td className="px-3 py-3">
          <SourceBadge source={issue.source} />
        </td>

        <td className="px-3 py-3 hidden lg:table-cell">
          <StatusBadge status={issue.status} />
        </td>

        <td className="px-3 py-3 hidden md:table-cell">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {formatDate(issue.created_at)}
          </span>
        </td>

        <td className="px-3 py-3">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500"
              title="Show address details"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {isActionable && (
              <>
                <button
                  onClick={handleGoToShippingLabels}
                  className="p-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-blue-600 dark:text-blue-400"
                  title="Fix in Shipping Labels"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setShowResolveModal(true)}
                  className="p-1.5 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors text-emerald-600 dark:text-emerald-400"
                  title="Resolve"
                >
                  <CheckCircle className="h-4 w-4" />
                </button>
                <button
                  onClick={() => onIgnore(issue.id)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-400"
                  title="Ignore"
                >
                  <EyeOff className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-gray-50/50 dark:bg-gray-800/30">
          <td colSpan={9} className="px-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Address
                </span>
                <p className="mt-1 text-gray-700 dark:text-gray-300">
                  {addressParts.length > 0 ? addressParts.join(', ') : 'No address data'}
                </p>
              </div>

              {addr.phone && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Phone
                  </span>
                  <p className="mt-1 text-gray-700 dark:text-gray-300">{addr.phone}</p>
                </div>
              )}

              {issue.error_code && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Error Code
                  </span>
                  <p className="mt-1">
                    <code className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs font-mono">
                      {issue.error_code}
                    </code>
                  </p>
                </div>
              )}

              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Full Error
                </span>
                <p className="mt-1 text-gray-600 dark:text-gray-400 text-xs leading-relaxed break-words">
                  {issue.issue_description}
                </p>
              </div>

              {issue.resolution_note && (
                <div className="sm:col-span-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Resolution Note
                  </span>
                  <p className="mt-1 text-emerald-700 dark:text-emerald-400 text-xs">
                    {issue.resolution_note}
                  </p>
                </div>
              )}

              {issue.email_sent_at && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Emailed At
                  </span>
                  <p className="mt-1 text-blue-600 dark:text-blue-400 text-xs">
                    {formatDate(issue.email_sent_at)}
                  </p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}

      {showResolveModal && (
        <ResolveNoteModal
          orderReference={issue.order_reference}
          onConfirm={(note) => {
            onResolve(issue.id, note);
            setShowResolveModal(false);
          }}
          onClose={() => setShowResolveModal(false)}
        />
      )}
    </>
  );
};
